import {AsyncButton} from "@client/components/Button";
import {Icon} from "@client/components/Icon";
import type * as constants from "@client/constants";
import * as analytics from "@client/shared/analytics";
import {Menu, MenuButton, MenuItem, MenuItems} from "@headlessui/react";
import {ChevronDownIcon} from "@heroicons/react/20/solid";
import {classNames} from "@reactivated";
import React from "react";

const typographyStyles = {
    Header: "text-[30px] text-center font-normal leading-[1.2] text-[#373A3C] [&>em]:text-[#EF8165] md:text-[35px] md:text-left lg:text-[42px]",
    Subheader:
        "text-[20px] text-center font-normal leading-[1.2] text-[#373A3C] [&>em]:text-[#EF8165] md:text-[24px] md:text-left lg:text-[30px]",
    Title: "font-medium text-xl leading-[1.2]",
    Subtitle: "font-medium text-base leading-[1.2]",
    P: "text-[13px] leading-[1.2] lg:text-sm [&>strong]:font-medium [&>a]:underline [&>a]:font-medium",
    small: "text-xs",
    bold: "font-medium",
    muted: "text-[#777777]",
    breaklessOnMobile: "[&_br]:hidden md:[&_br]:inline",
    A: "underline font-medium",
    tableWrapper: "overflow-x-auto",
    Table: "whitespace-nowrap text-[13px] w-full border-collapse [&_th]:p-[5px] [&_td]:p-[5px] [&_th]:font-medium",
    legacyPage: "legacy-page",
};

export {Icon, SimpleIcon} from "@client/components/Icon";
export {Button, ButtonGroup, AsyncButton} from "@client/components/Button";
export {Prose} from "@client/components/Prose";

export const Header = (props: {children: React.ReactNode}) => (
    <h1 className={typographyStyles.Header}>{props.children}</h1>
);

export const Subheader = (props: {children: React.ReactNode}) => (
    <h2 className={typographyStyles.Subheader}>{props.children}</h2>
);

export const P = (props: {
    muted?: boolean;
    small?: boolean;
    bold?: boolean;
    breaklessOnMobile?: boolean;
    children: React.ReactNode;
}) => (
    <p
        className={classNames(
            typographyStyles.P,
            props.muted === true && typographyStyles.muted,
            props.small === true && typographyStyles.small,
            props.bold === true && typographyStyles.bold,
            props.breaklessOnMobile === true && typographyStyles.breaklessOnMobile,
        )}
    >
        {props.children}
    </p>
);

export const A = (props: {
    track?: string;
    className?: string;
    href: string;
    external?: boolean;
    children: React.ReactNode;
}) => {
    const onClick =
        props.track != null
            ? analytics.trackLink(props.track, props.external)
            : undefined;

    return (
        <a
            href={props.href}
            onClick={onClick}
            className={props.className ?? typographyStyles.A}
        >
            {props.children}
        </a>
    );
};
export const Table = (props: {children: React.ReactNode}) => (
    <div className={typographyStyles.tableWrapper}>
        <table className={typographyStyles.Table}>{props.children}</table>
    </div>
);

export const Title = (props: {children?: React.ReactNode}) => (
    <div className={typographyStyles.Title}>{props.children}</div>
);

export const Subtitle = (props: {muted?: boolean; children?: React.ReactNode}) => (
    <div
        className={classNames(
            typographyStyles.Subtitle,
            props.muted === true && typographyStyles.muted,
        )}
    >
        {props.children}
    </div>
);

interface DropdownButtonProps {
    onClick: () => void;
    items: Array<{
        icon: constants.Icon;
        title: string;
        onClick: () => unknown;
    }>;
    children: React.ReactNode;
}

export function DropdownButton(props: DropdownButtonProps) {
    return (
        <div className="inline-flex rounded-md">
            <button
                type="button"
                onClick={props.onClick}
                className="relative inline-flex items-center rounded-l-md bg-indigo-600 px-3 py-1 text-xs font-semibold text-white hover:bg-indigo-500 focus:z-10"
            >
                {props.children}
            </button>
            <Menu as="div" className="relative ml-px block">
                <MenuButton className="relative inline-flex h-full items-center rounded-r-md bg-indigo-600 px-1 text-white hover:bg-indigo-500 focus:z-10">
                    <span className="sr-only">Open options</span>
                    <ChevronDownIcon aria-hidden="true" className="size-4" />
                </MenuButton>
                <MenuItems
                    transition
                    className="absolute right-0 z-10 mt-2 -mr-1 w-56 origin-top-right rounded-md bg-white shadow-lg ring-1 ring-black/5 transition focus:outline-hidden data-closed:scale-95 data-closed:transform data-closed:opacity-0 data-enter:duration-100 data-enter:ease-out data-leave:duration-75 data-leave:ease-in"
                >
                    <div className="py-1">
                        {props.items.map((item, index) => (
                            <MenuItem key={index}>
                                <button
                                    onClick={item.onClick}
                                    className="block w-full px-4 py-2 text-left text-sm text-gray-900 data-focus:bg-gray-100 data-focus:text-gray-900"
                                >
                                    <Icon icon={item.icon}>{item.title}</Icon>
                                </button>
                            </MenuItem>
                        ))}
                    </div>
                </MenuItems>
            </Menu>
        </div>
    );
}

export const ImportantAction = (props: {
    title: string;
    showIf?: boolean;
    onClick: () => void;
    subtitle?: string;
    forceLoading?: boolean;
}) => {
    if (props.showIf == false) {
        return null;
    }

    return (
        <AsyncButton
            type="button"
            className="col-span-1 flex w-full items-center justify-center rounded-lg border-2 border-dashed border-indigo-400 bg-white p-8 text-sm font-semibold text-indigo-800 shadow-xs"
            onClick={props.onClick}
            forceLoading={props.forceLoading}
        >
            {props.title}
            {props.subtitle != null && (
                <>
                    <br />
                    {props.subtitle}
                </>
            )}
        </AsyncButton>
    );
};
